/**
 * Distance formatting utilities
 * Consistent formatting for displaying distances across the application
 */

/**
 * Format distance in meters with appropriate units
 * Automatically switches between m and km based on distance
 *
 * @param meters - Distance in meters
 * @param precision - Number of decimal places (default: 2)
 * @returns Formatted string with units (e.g., "15.50m" or "1.25km")
 */
export function formatDistance(meters: number, precision: number = 2): string {
  if (meters < 1000) {
    return `${meters.toFixed(precision)}m`;
  } else {
    const kilometers = meters / 1000;
    return `${kilometers.toFixed(precision)}km`;
  }
}

/**
 * Format distance always in meters
 *
 * @param meters - Distance in meters
 * @param precision - Number of decimal places (default: 2)
 * @returns Formatted string with 'm' unit
 */
export function formatMeters(meters: number, precision: number = 2): string {
  return `${meters.toFixed(precision)}m`;
}

/**
 * Format distance always in kilometers
 *
 * @param meters - Distance in meters
 * @param precision - Number of decimal places (default: 3)
 * @returns Formatted string with 'km' unit
 */
export function formatKilometers(meters: number, precision: number = 3): string {
  const kilometers = meters / 1000;
  return `${kilometers.toFixed(precision)}km`;
}

/**
 * Parse distance string back to meters
 * Handles "15m", "1.5km", "15.5 m", etc.
 *
 * @param distanceStr - Distance string with units
 * @returns Distance in meters, or null if invalid
 */
export function parseDistance(distanceStr: string): number | null {
  const match = distanceStr.match(/^([\d.]+)\s*(m|km)$/i);
  if (!match) return null;

  const value = parseFloat(match[1]);
  const unit = match[2].toLowerCase();

  if (unit === 'km') {
    return value * 1000;
  }
  return value;
}
